/**
 * One tractor's day as two lanes positioned by time (SW-8879): production
 * readiness on top — each red segment names its reason and carries the note
 * — and lake activity below. On today, everything after now is hatched on
 * the top lane and everything after the lake's data-through time on the
 * bottom one. Markers above the lanes are Jira tickets and takeovers. Moving
 * the mouse over the lanes shows the time under the cursor and both states.
 */
import React, { useRef, useState } from "react";
import { styled } from "styled-components";
import { HATCH, LANE_COLORS } from "./lanes";
import { primaryReason, reasonLabel } from "./labels";
import { clock } from "./time";
import { Interval, Point } from "./types";
import { tokens } from "../TractorState/tokens";

export interface ReadinessTimelineProps {
  dayStart: string;
  dayEnd: string;
  tz: string;
  readiness: Interval[];
  activity: Interval[];
  points?: Point[];
  /** Accessible names: "<name> readiness on <day>", "<name> activity on <day>". */
  name: string;
  day: string;
  /** Now, when the day is still running; null for a complete day. */
  nowIso?: string | null;
  /** How far the lake has written; null when nothing has arrived. */
  dataThrough?: string | null;
}

const ACTIVITY_LABEL: Record<string, string> = {
  AVAILABLE: "Available",
  BUSY: "Busy (on a job)",
  OFFLINE: "Offline",
};

function readinessText(segment: Interval): string {
  if (segment.state === "READY") return "Production ready";
  if (segment.state === "UNSET") return "No readiness mark";
  const reason = primaryReason(segment.flags);
  return reason ? `Not ready · ${reasonLabel(reason)}` : "Not ready";
}

function segmentLabel(segment: Interval): string {
  if (segment.state === "READY") return "Production ready";
  if (segment.state === "UNSET") return "No readiness mark in Tractor State";
  const reason = primaryReason(segment.flags);
  const head = reason ? reasonLabel(reason) : "Not ready";
  return segment.note ? `${head} · ${segment.note}` : head;
}

const readinessColor = (state: string) =>
  state === "READY"
    ? LANE_COLORS.f
    : state === "UNSET"
      ? LANE_COLORS.u
      : LANE_COLORS.n;
const activityColor = (state: string) =>
  state === "BUSY"
    ? LANE_COLORS.b
    : state === "AVAILABLE"
      ? LANE_COLORS.a
      : LANE_COLORS.o;

const TIP_WIDTH = 280;

interface Hover {
  x: number;
  pct: number;
  tipLeft: number;
  time: string;
  readiness: Interval | undefined;
  activity: Interval | undefined;
  notYet: boolean;
}

const ReadinessTimeline: React.FC<ReadinessTimelineProps> = ({
  dayStart,
  dayEnd,
  tz,
  readiness,
  activity,
  points = [],
  name,
  day,
  nowIso = null,
  dataThrough = null,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const startMs = Date.parse(dayStart);
  const totalMs = Math.max(1, Date.parse(dayEnd) - startMs);
  const pct = (iso: string) =>
    Math.min(100, Math.max(0, ((Date.parse(iso) - startMs) / totalMs) * 100));
  const fmt = (n: number) => `${n.toFixed(2)}%`;
  const nowPct =
    nowIso && Date.parse(nowIso) < Date.parse(dayEnd) ? pct(nowIso) : null;
  const throughPct =
    dataThrough === null
      ? 0
      : Date.parse(dataThrough) < Date.parse(dayEnd)
        ? pct(dataThrough)
        : null;

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const fraction = x / rect.width;
    const at = new Date(startMs + fraction * totalMs);
    const atIso = at.toISOString();
    const within = (s: Interval) =>
      Date.parse(s.start) <= at.getTime() && at.getTime() < Date.parse(s.end);
    setHover({
      x,
      pct: fraction * 100,
      tipLeft: Math.max(0, Math.min(x - TIP_WIDTH / 2, rect.width - TIP_WIDTH)),
      time: clock(atIso, tz),
      readiness: readiness.find(within),
      activity: activity.find(within),
      notYet: nowPct !== null && fraction * 100 >= nowPct,
    });
  };

  const hourMarks = Array.from({ length: 7 }, (_, i) => i * 4);

  return (
    <Wrap>
      {hover && (
        <>
          <Tip style={{ left: hover.tipLeft }} aria-hidden="true">
            <TipTime>{hover.time}</TipTime>
            {hover.notYet ? (
              <TipNote>Not yet.</TipNote>
            ) : (
              <>
                <TipState
                  style={{
                    color: hover.readiness
                      ? readinessColor(hover.readiness.state)
                      : tokens.slate,
                  }}
                >
                  {hover.readiness
                    ? readinessText(hover.readiness)
                    : "No readiness data"}
                </TipState>
                {hover.readiness?.note && (
                  <TipNote>{hover.readiness.note}</TipNote>
                )}
                <TipActivity>
                  <strong>Activity:</strong>{" "}
                  {hover.activity
                    ? (ACTIVITY_LABEL[hover.activity.state] ??
                      hover.activity.state)
                    : "no lake data"}
                </TipActivity>
              </>
            )}
          </Tip>
          <Playhead style={{ left: `${hover.pct}%` }} aria-hidden="true" />
        </>
      )}
      <Markers aria-hidden="true">
        {points.map((p, i) => (
          <Marker
            key={`${p.kind}-${i}`}
            style={{ left: `${pct(p.ts)}%` }}
            title={markerTitle(p, tz)}
          />
        ))}
      </Markers>
      <Lanes
        ref={trackRef}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {nowPct !== null && (
          <NowLine
            style={{ left: `${nowPct}%` }}
            data-testid="now-line"
            aria-hidden="true"
          >
            <span>now</span>
          </NowLine>
        )}
        <Lane $tall role="img" aria-label={`${name} readiness on ${day}`}>
          {readiness.map((s) => {
            const left = pct(s.start);
            const width = pct(s.end) - left;
            if (width <= 0) return null;
            return (
              <Segment
                key={`${s.start}-${s.state}`}
                data-testid="segment"
                data-state={s.state}
                title={readinessText(s) + (s.note ? ` — ${s.note}` : "")}
                style={{
                  left: fmt(left),
                  width: fmt(width),
                  background: readinessColor(s.state),
                  color:
                    s.state === "READY"
                      ? "#0b3d16"
                      : s.state === "UNSET"
                        ? tokens.ink
                        : "#fff",
                }}
              >
                {width >= 8 ? segmentLabel(s) : ""}
              </Segment>
            );
          })}
          {nowPct !== null && (
            <NotYet
              data-testid="not-yet"
              style={{ left: fmt(nowPct), width: fmt(100 - nowPct) }}
            />
          )}
        </Lane>
        <Lane role="img" aria-label={`${name} activity on ${day}`}>
          {activity.map((s) => {
            const left = pct(s.start);
            const width = pct(s.end) - left;
            if (width <= 0) return null;
            return (
              <Segment
                key={`${s.start}-${s.state}`}
                data-testid="segment"
                data-state={s.state}
                title={ACTIVITY_LABEL[s.state] ?? s.state}
                style={{
                  left: fmt(left),
                  width: fmt(width),
                  background: activityColor(s.state),
                }}
              />
            );
          })}
          {throughPct !== null && (
            <NotYet
              data-testid="no-data"
              style={{ left: fmt(throughPct), width: fmt(100 - throughPct) }}
            />
          )}
        </Lane>
      </Lanes>
      <Axis aria-hidden="true">
        {hourMarks.map((h) => (
          <span key={h}>{String(h).padStart(2, "0")}:00</span>
        ))}
      </Axis>
    </Wrap>
  );
};

export default ReadinessTimeline;

function markerTitle(p: Point, tz: string): string {
  const when = clock(p.ts, tz);
  if (p.kind === "ticket") {
    const t = p as Point & { key?: string; summary?: string; closed?: boolean };
    return `${when} · ${t.key ?? "Ticket"}${t.closed ? " (closed)" : ""}${t.summary ? ` · ${t.summary}` : ""}`;
  }
  if (p.kind === "takeover") return `${when} · Takeover`;
  return `${when} · ${p.kind}${p.note ? ` · ${p.note}` : ""}`;
}

const Wrap = styled.div`
  position: relative;
  padding-top: 14px;
`;

const Markers = styled.div`
  position: relative;
  height: 8px;
`;

const Marker = styled.div`
  position: absolute;
  top: 0;
  width: 0;
  height: 0;
  transform: translateX(-5px);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid ${tokens.ink};
`;

const Lanes = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: crosshair;
`;

const Lane = styled.div<{ $tall?: boolean }>`
  position: relative;
  height: ${(p) => (p.$tall ? 22 : 14)}px;
  border-radius: 5px;
  overflow: hidden;
  background: ${LANE_COLORS.o};
  box-shadow: inset 0 1px 2px rgba(34, 42, 53, 0.1);
`;

const Segment = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 6px;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  font: 600 11px ${tokens.fontSans};
`;

const NotYet = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: ${HATCH};
`;

const NowLine = styled.div`
  position: absolute;
  top: -12px;
  bottom: 0;
  width: 2px;
  transform: translateX(-1px);
  background: ${tokens.navy};
  z-index: 2;
  pointer-events: none;
  span {
    position: absolute;
    top: 0;
    left: 4px;
    font: 600 10px ${tokens.fontMono};
    color: ${tokens.navy};
  }
`;

const Playhead = styled.div`
  position: absolute;
  top: 22px;
  bottom: 20px;
  width: 2px;
  transform: translateX(-1px);
  background: ${tokens.ink};
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.4);
  z-index: 5;
  pointer-events: none;
`;

const Axis = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font: 11px ${tokens.fontMono};
  color: ${tokens.slate};
`;

const Tip = styled.div`
  position: absolute;
  bottom: calc(100% - 12px);
  z-index: 6;
  width: ${TIP_WIDTH}px;
  padding: 10px 12px;
  pointer-events: none;
  border: 1px solid ${tokens.line};
  border-radius: 10px;
  background: ${tokens.white};
  box-shadow: ${tokens.shadowMd};
  font-family: ${tokens.fontSans};
`;

const TipTime = styled.div`
  margin-bottom: 6px;
  font: 11px ${tokens.fontMono};
  letter-spacing: 0.04em;
  color: ${tokens.slate};
`;

const TipState = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const TipNote = styled.div`
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: ${tokens.ink};
`;

const TipActivity = styled.div`
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid ${tokens.line};
  font-size: 12px;
  color: ${tokens.ink};
`;
